import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Text,
  Checkbox,
  Dropdown,
  OnboardingHeader,
  Button,
  RadioButton,
  TextareaWithLabel,
  Modal,
  InfoIconWithBackground,
} from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import {
  serviceOptions,
  getEngagementOptions,
  getBudgetOptions,
  getEstablishmentTimeframeOptions,
} from '../../../shared/constants';
import { TermsAndConditionsModal } from '../../../shared/components/TermsAndConditionsModal';
import { createServiceNeedsSchema } from '../schemas/service-needs-schema';
import { useServiceNeedsStore } from '../stores/service-needs-store';
import { useCompanyTypeStore } from '../../company-type/stores/company-type-store';
import type { ServiceNeedsFormData } from '@tasama/types/domain';

const SERVICE_NEEDS_DEFAULT_VALUES: ServiceNeedsFormData = {
  selectedServices: [],
  engagementTimeframe: '',
  budgetAllocated: '',
  acceptTerms: false,
  establishmentTimeframe: '',
  goalsExpectations: '',
};

interface ServiceNeedsProps {
  testID?: string | undefined;
}

const ServiceNeeds: React.FC<ServiceNeedsProps> = ({
  testID = 'service-needs',
}) => {
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const { formData: companyTypeData } = useCompanyTypeStore();
  const { companyType, companyStatus } = companyTypeData;
  const isInternationalMovingToKSA = companyType === 'INTERNATIONAL';

  const { t } = useTranslation('onboarding');

  const translatedEstablishmentOptions = useMemo(
    () => getEstablishmentTimeframeOptions(t),
    [t]
  );
  const translatedEngagementOptions = useMemo(
    () => getEngagementOptions(t),
    [t]
  );
  const translatedBudgetOptions = useMemo(() => getBudgetOptions(t), [t]);

  // Dynamic schema — switches between international and standard form
  const schema = useMemo(
    () => createServiceNeedsSchema(companyType, companyStatus, t),
    [companyType, companyStatus, t]
  );

  // Read persisted data — restores fields when navigating back to this step
  const storedFormData = useServiceNeedsStore.getState().formData;

  const {
    watch,
    control,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<ServiceNeedsFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    mode: 'onTouched',
    defaultValues: { ...SERVICE_NEEDS_DEFAULT_VALUES, ...storedFormData },
  });

  // Coordinator store sync — keeps use-step-validation and use-onboarding-payload working
  const setStoreValidity = useServiceNeedsStore((s: any) => s.setValidity);
  const setStoreFormData = useServiceNeedsStore((s: any) => s.setFormData);

  useEffect(() => {
    setStoreValidity(isValid);
  }, [isValid, setStoreValidity]);

  useEffect(() => {
    const subscription = watch((values) => {
      setStoreFormData(values as Partial<ServiceNeedsFormData>);
    });
    return () => subscription.unsubscribe();
  }, [watch, setStoreFormData]);

  const formValues = useWatch({ control });
  const selectedServices = formValues.selectedServices ?? [];
  const engagementTimeframe = formValues.engagementTimeframe ?? '';
  const budgetAllocated = formValues.budgetAllocated ?? '';
  const acceptTerms = formValues.acceptTerms ?? false;
  const establishmentTimeframe = formValues.establishmentTimeframe ?? '';
  const goalsExpectations = formValues.goalsExpectations ?? '';

  const handleServiceToggle = (serviceId: string) => {
    const next = selectedServices.includes(serviceId)
      ? selectedServices.filter((id) => id !== serviceId)
      : [...selectedServices, serviceId];
    setValue('selectedServices', next, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleEngagementChange = (value: string) => {
    setValue('engagementTimeframe', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleBudgetChange = (value: string) => {
    setValue('budgetAllocated', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setValue('acceptTerms', checked, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleEstablishmentTimeframeChange = (value: string) => {
    setValue('establishmentTimeframe', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleGoalsExpectationsChange = (value: string) => {
    setValue('goalsExpectations', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return (
    <>
      <OnboardingHeader
        icon={'FileText'}
        size="lg"
        title={t('serviceNeeds.title')}
        description={t('serviceNeeds.description')}
        testID={'service-need'}
      />
      <div className="space-y-4 md:space-y-6">
        {isInternationalMovingToKSA ? (
          <>
            <div className="space-y-4">
              <Dropdown
                testID={'establishment-timeframe-dropdown'}
                label={t('serviceNeeds.establishmentTimeframe.label')}
                placeholder={t(
                  'serviceNeeds.establishmentTimeframe.placeholder'
                )}
                options={translatedEstablishmentOptions}
                value={establishmentTimeframe}
                showLeftIcon={false}
                onChange={handleEstablishmentTimeframeChange}
                onBlur={() => void trigger('establishmentTimeframe')}
                error={!!errors.establishmentTimeframe}
                {...(errors.establishmentTimeframe && {
                  errorMessage: errors.establishmentTimeframe.message,
                })}
              />
            </div>

            <div className="space-y-2">
              <TextareaWithLabel
                testID={'goals-expectations-textarea'}
                label={t('serviceNeeds.goalsExpectations.label')}
                id="goals-expectations"
                placeholder={t('serviceNeeds.goalsExpectations.placeholder')}
                value={goalsExpectations}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleGoalsExpectationsChange(e.target.value)
                }
                error={!!errors.goalsExpectations}
                {...(errors.goalsExpectations && {
                  errorMessage: errors.goalsExpectations.message,
                })}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <Checkbox
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={() => handleTermsChange(!acceptTerms)}
                  testID="accept-terms"
                  className="shrink-0"
                />
                <Text className="text-sm text-fg-secondary font-normal">
                  {t('serviceNeeds.terms.text')}{' '}
                  <Button
                    variant="quaternary"
                    className="p-0 h-auto"
                    onClick={() => setIsTermsModalOpen(true)}
                    label={t('serviceNeeds.terms.link')}
                  />
                </Text>
              </label>
              {errors.acceptTerms && (
                <Text className="text-sm text-fg-error-primary">
                  {errors.acceptTerms.message}
                </Text>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Text className="text-sm font-normal text-fg-secondary">
                {t('serviceNeeds.servicesLabel')}
              </Text>
              <div className="grid lg:grid-cols-3 lg:gap-6 sm:gap-3">
                <div className="space-y-3">
                  {serviceOptions.slice(0, 5).map((service) => (
                    <label
                      key={service.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedServices.includes(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                        testID={`service-${service.id}`}
                        className="shrink-0"
                      />
                      <Text className="text-sm text-fg-secondary font-normal leading-5">
                        {t(`serviceNeeds.services.${service.id}`)}
                      </Text>
                    </label>
                  ))}
                </div>

                <div className="space-y-3">
                  {serviceOptions.slice(5, 9).map((service) => (
                    <label
                      key={service.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedServices.includes(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                        testID={`service-${service.id}`}
                        className="shrink-0"
                      />
                      <Text className="text-sm text-fg-secondary font-normal leading-5">
                        {t(`serviceNeeds.services.${service.id}`)}
                      </Text>
                    </label>
                  ))}
                </div>

                <div className="space-y-3">
                  {serviceOptions
                    .slice(9, serviceOptions.length)
                    .map((service) => (
                      <label
                        key={service.id}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedServices.includes(service.id)}
                          onChange={() => handleServiceToggle(service.id)}
                          testID={`service-${service.id}`}
                          className="shrink-0"
                        />
                        <Text className="text-sm text-fg-secondary font-normal leading-5">
                          {t(`serviceNeeds.services.${service.id}`)}
                        </Text>
                      </label>
                    ))}
                </div>
              </div>
              {errors.selectedServices && (
                <Text className="text-sm text-fg-error-primary">
                  {errors.selectedServices.message}
                </Text>
              )}
            </div>

            <div className="lg:space-y-2">
              <Text className="text-sm font-normal text-fg-secondary">
                {t('serviceNeeds.engagementTimeframe.label')}
              </Text>
              <div className="px-1">
                <Dropdown
                  testID={'engagement-timeframe-dropdown'}
                  placeholder={t(
                    'serviceNeeds.engagementTimeframe.placeholder'
                  )}
                  options={translatedEngagementOptions}
                  value={engagementTimeframe}
                  showLeftIcon={false}
                  onChange={handleEngagementChange}
                  error={!!errors.engagementTimeframe}
                  {...(errors.engagementTimeframe && {
                    errorMessage: errors.engagementTimeframe.message,
                  })}
                />
              </div>
            </div>

            <div className="lg:space-y-2">
              <div className="flex flex-col">
                <div className="flex-1">
                  <Text className="text-sm font-normal text-fg-secondary mb-2">
                    {t('serviceNeeds.budget.label')}
                  </Text>
                </div>
                <div className="flex gap-6">
                  {translatedBudgetOptions.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <RadioButton
                        name="budget"
                        value={option.value}
                        checked={budgetAllocated === option.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleBudgetChange(e.target.value)
                        }
                        uiSize="sm"
                        testID={`budget-${option.value}`}
                      />
                      <Text className="text-sm text-fg-secondary font-normal">
                        {option.label}
                      </Text>
                    </label>
                  ))}
                </div>
                {errors.budgetAllocated && (
                  <Text className="mt-1 text-sm text-fg-error-primary">
                    {errors.budgetAllocated.message}
                  </Text>
                )}
              </div>
            </div>

            <div className="lg:space-y-2">
              <label className="flex items-center gap-2">
                <Checkbox
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={() => handleTermsChange(!acceptTerms)}
                  testID="accept-terms"
                  className="shrink-0"
                />
                <Text className="text-sm text-fg-secondary font-normal">
                  {t('serviceNeeds.terms.text')}{' '}
                  <Button
                    variant="quaternary"
                    className="p-0 h-auto"
                    onClick={() => setIsTermsModalOpen(true)}
                    label={t('serviceNeeds.terms.link')}
                  />
                </Text>
              </label>
              {errors.acceptTerms && (
                <Text className="text-sm text-fg-error-primary">
                  {errors.acceptTerms.message}
                </Text>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        size="lg"
        testID="terms-conditions-modal"
      >
        <Modal.Header
          icon={
            <InfoIconWithBackground
              size="large"
              testID={`${testID}-modal-icon`}
            />
          }
          title={t('termsAndConditions.title')}
          type="left"
          showCloseButton={true}
        />
        <Modal.Content className="max-h-[60vh] overflow-y-auto pt-0">
          <TermsAndConditionsModal testID="terms-conditions-content" />
        </Modal.Content>
      </Modal>
    </>
  );
};

export default ServiceNeeds;
export type { ServiceNeedsProps };
